import React, { useState, useEffect, useCallback } from 'react'
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  TextInput,
  Alert,
  Platform,
  RefreshControl,
} from 'react-native'
import {
  ArrowLeft,
  RefreshCw,
  Search,
  X,
  Check,
  ChevronDown,
  ChevronUp,
  Ban,
  RotateCcw,
  Receipt,
  CreditCard,
  Banknote,
} from 'lucide-react-native'
import { NativeStackNavigationProp } from '@react-navigation/native-stack'
import { RouteProp } from '@react-navigation/native'
import { RootStackParamList } from '../types/navigation'
import { supabase } from '../lib/supabase'
import { useLanguage } from '@fliisi/i18n'
import { useToast } from '../components/Toast'
import { Spinner, YStack } from '@fliisi/pollen'

const merchantColors = {
  primary: '#C07A5F',
  primaryLight: '#D4A088',
  accent: '#748C70',
}

interface Props {
  navigation: NativeStackNavigationProp<RootStackParamList, 'StoreTransactions'>
  route: RouteProp<RootStackParamList, 'StoreTransactions'>
}

interface SaleItem {
  id: string
  sale_id: string
  product_id: string
  price: number
  seller_id: string
  refunded: boolean
  refund_amount: number | null
  refunded_at: string | null
  product: {
    id: string
    title: string
    title_fi: string | null
    title_en: string | null
    barcode: string | null
  } | null
}

interface SalePayment {
  id: string
  payment_method: string
  amount: number
  status: string
}

interface Sale {
  id: string
  receipt_number: string | null
  total: number
  subtotal: number
  discount_amount: number
  voided_at: string | null
  void_reason: string | null
  created_at: string
  sale_items: SaleItem[]
  sale_payments: SalePayment[]
}

interface DayGroup {
  label: string
  sales: Sale[]
}

export default function StoreTransactionsScreen({ navigation, route }: Props) {
  const { storeId } = route.params
  const { t, language } = useLanguage()
  const toast = useToast()

  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [sales, setSales] = useState<Sale[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [expandedSale, setExpandedSale] = useState<string | null>(null)
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set())
  const [processing, setProcessing] = useState(false)
  const [voidReason, setVoidReason] = useState('')

  const fetchSales = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('sales')
        .select(`
          id,
          receipt_number,
          total,
          subtotal,
          discount_amount,
          voided_at,
          void_reason,
          created_at,
          sale_items (
            id,
            sale_id,
            product_id,
            price,
            seller_id,
            refunded,
            refund_amount,
            refunded_at,
            product:products (
              id,
              title,
              title_fi,
              title_en,
              barcode
            )
          ),
          sale_payments (
            id,
            payment_method,
            amount,
            status
          )
        `)
        .eq('flea_market_id', storeId)
        .order('created_at', { ascending: false })
        .limit(200)

      if (error) throw error

      const formatted: Sale[] = (data || []).map((sale: any) => ({
        ...sale,
        sale_items: (sale.sale_items || []).map((item: any) => ({
          ...item,
          product: Array.isArray(item.product) ? item.product[0] : item.product,
        })),
      }))

      setSales(formatted)
    } catch (error: any) {
      console.error('Error fetching transactions:', error)
      toast.show(t('error') || 'Error', 'error')
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [storeId])

  useEffect(() => {
    fetchSales()
  }, [fetchSales])

  const showAlert = (title: string, message: string) => {
    if (Platform.OS === 'web') {
      window.alert(`${title}: ${message}`)
    } else {
      Alert.alert(title, message)
    }
  }

  const confirmAlert = (title: string, message: string, onConfirm: () => void) => {
    if (Platform.OS === 'web') {
      if (window.confirm(`${title}\n\n${message}`)) {
        onConfirm()
      }
    } else {
      Alert.alert(title, message, [
        { text: t('cancel') || 'Cancel', style: 'cancel' },
        { text: t('confirm') || 'Confirm', style: 'destructive', onPress: onConfirm },
      ])
    }
  }

  const getProductTitle = (item: SaleItem) => {
    if (!item.product) return 'Unknown'
    return language === 'fi'
      ? item.product.title_fi || item.product.title_en || item.product.title || 'Unknown'
      : item.product.title_en || item.product.title_fi || item.product.title || 'Unknown'
  }

  const getPaymentMethod = (sale: Sale): string => {
    if (sale.sale_payments.length === 0) return 'unknown'
    return sale.sale_payments[0].payment_method
  }

  const getPaymentLabel = (method: string): string => {
    switch (method) {
      case 'cash': return t('cash') || 'Cash'
      case 'card': return t('card') || 'Card'
      case 'stripe_terminal': return 'Terminal'
      default: return method
    }
  }

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr)
    return date.toLocaleTimeString(language === 'fi' ? 'fi-FI' : 'en-US', {
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const getDayLabel = (dateStr: string): string => {
    const date = new Date(dateStr)
    const now = new Date()
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    const yesterday = new Date(today)
    yesterday.setDate(yesterday.getDate() - 1)
    const saleDay = new Date(date.getFullYear(), date.getMonth(), date.getDate())

    if (saleDay.getTime() === today.getTime()) return t('today') || 'Today'
    if (saleDay.getTime() === yesterday.getTime()) return t('yesterday') || 'Yesterday'
    return date.toLocaleDateString(language === 'fi' ? 'fi-FI' : 'en-US', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    })
  }

  const filteredSales = sales.filter((sale) => {
    if (!searchQuery) return true
    const q = searchQuery.toLowerCase()
    return (
      (sale.receipt_number || '').toLowerCase().includes(q) ||
      sale.sale_items.some((item) => getProductTitle(item).toLowerCase().includes(q))
    )
  })

  const groupedSales: DayGroup[] = (() => {
    const groups: Record<string, Sale[]> = {}
    const order: string[] = []
    for (const sale of filteredSales) {
      const key = new Date(sale.created_at).toDateString()
      if (!groups[key]) {
        groups[key] = []
        order.push(key)
      }
      groups[key].push(sale)
    }
    return order.map((key) => ({
      label: getDayLabel(groups[key][0].created_at),
      sales: groups[key],
    }))
  })()

  const handleVoidSale = (sale: Sale) => {
    if (Platform.OS === 'web') {
      const reason = window.prompt(
        `${t('void_sale') || 'Void Sale'}\n\n${t('void_reason_hint') || 'Why is this sale being voided?'}`
      )
      if (reason !== null) {
        performVoid(sale, reason)
      }
    } else {
      Alert.prompt?.(
        t('void_sale') || 'Void Sale',
        t('void_reason_hint') || 'Why is this sale being voided?',
        [
          { text: t('cancel') || 'Cancel', style: 'cancel' },
          {
            text: t('confirm_void') || 'Confirm Void',
            style: 'destructive',
            onPress: (reason) => performVoid(sale, reason || ''),
          },
        ],
        'plain-text'
      ) ?? confirmAlert(
        t('void_sale') || 'Void Sale',
        t('void_reason_hint') || 'Why is this sale being voided?',
        () => performVoid(sale, '')
      )
    }
  }

  const performVoid = async (sale: Sale, reason: string) => {
    setProcessing(true)
    try {
      const now = new Date().toISOString()

      // 1. Void the sale
      const { error: saleError } = await supabase
        .from('sales')
        .update({ voided_at: now, void_reason: reason || null })
        .eq('id', sale.id)
      if (saleError) throw saleError

      // 2. Mark all non-refunded items as refunded
      const nonRefundedItems = sale.sale_items.filter((item) => !item.refunded)
      for (const item of nonRefundedItems) {
        const { error: itemError } = await supabase
          .from('sale_items')
          .update({ refunded: true, refunded_at: now, refund_amount: item.price })
          .eq('id', item.id)
        if (itemError) throw itemError

        // 3. Set products back to for_sale
        if (item.product_id) {
          await supabase
            .from('products')
            .update({ status: 'for_sale' })
            .eq('id', item.product_id)
        }
      }

      toast.show(t('sale_void_success') || 'Sale voided successfully', 'success')
      fetchSales()
    } catch (error: any) {
      console.error('Void error:', error)
      showAlert(t('error') || 'Error', error.message)
    } finally {
      setProcessing(false)
    }
  }

  const toggleItemSelection = (itemId: string) => {
    const next = new Set(selectedItems)
    if (next.has(itemId)) next.delete(itemId)
    else next.add(itemId)
    setSelectedItems(next)
  }

  const handlePartialReturn = (sale: Sale) => {
    const returnable = sale.sale_items.filter((i) => !i.refunded)
    if (returnable.length === 0) return

    const selected = returnable.filter((i) => selectedItems.has(i.id))
    if (selected.length === 0) {
      showAlert(t('error') || 'Error', t('select_items_to_return') || 'Select items to return')
      return
    }

    confirmAlert(
      t('partial_return') || 'Return Items',
      `${t('return_selected') || 'Return Selected'} (${selected.length})`,
      () => performPartialReturn(sale, selected)
    )
  }

  const performPartialReturn = async (sale: Sale, items: SaleItem[]) => {
    setProcessing(true)
    try {
      const now = new Date().toISOString()
      for (const item of items) {
        const { error } = await supabase
          .from('sale_items')
          .update({ refunded: true, refunded_at: now, refund_amount: item.price })
          .eq('id', item.id)
        if (error) throw error

        if (item.product_id) {
          await supabase
            .from('products')
            .update({ status: 'for_sale' })
            .eq('id', item.product_id)
        }
      }

      toast.show(t('items_returned') || 'Items returned', 'success')
      setSelectedItems(new Set())
      setExpandedSale(null)
      fetchSales()
    } catch (error: any) {
      console.error('Return error:', error)
      showAlert(t('error') || 'Error', error.message)
    } finally {
      setProcessing(false)
    }
  }

  const toggleExpand = (saleId: string) => {
    if (expandedSale === saleId) {
      setExpandedSale(null)
      setSelectedItems(new Set())
    } else {
      setExpandedSale(saleId)
      setSelectedItems(new Set())
    }
  }

  const renderSaleCard = (sale: Sale) => {
    const isVoided = !!sale.voided_at
    const isExpanded = expandedSale === sale.id
    const paymentMethod = getPaymentMethod(sale)
    const itemCount = sale.sale_items.length
    const hasReturnableItems = sale.sale_items.some((i) => !i.refunded)
    const allRefunded = sale.sale_items.every((i) => i.refunded)

    return (
      <View key={sale.id} style={[styles.saleCard, isVoided && styles.saleCardVoided]}>
        <TouchableOpacity
          style={styles.saleHeader}
          onPress={() => toggleExpand(sale.id)}
          activeOpacity={0.7}
        >
          <View style={styles.saleHeaderLeft}>
            <View style={styles.receiptRow}>
              <Receipt size={14} color={isVoided ? '#999' : merchantColors.primary} />
              <Text style={[styles.receiptNumber, isVoided && styles.dimmedText]}>
                #{sale.receipt_number || sale.id.slice(-8)}
              </Text>
              {isVoided && (
                <View style={styles.voidedBadge}>
                  <Text style={styles.voidedBadgeText}>{t('sale_voided') || 'Voided'}</Text>
                </View>
              )}
            </View>
            <Text style={styles.saleTime}>{formatTime(sale.created_at)}</Text>
            <Text style={styles.itemCount}>
              {itemCount} {itemCount === 1 ? (t('item') || 'item') : (t('items') || 'items')}
            </Text>
          </View>
          <View style={styles.saleHeaderRight}>
            <Text style={[styles.saleTotal, isVoided && styles.totalStrikethrough]}>
              {sale.total.toFixed(2)} €
            </Text>
            <View
              style={[
                styles.paymentBadge,
                paymentMethod === 'cash' && styles.cashBadge,
                paymentMethod === 'card' && styles.cardBadge,
                paymentMethod === 'stripe_terminal' && styles.terminalBadge,
              ]}
            >
              {paymentMethod === 'cash' ? (
                <Banknote size={10} color="#666" />
              ) : (
                <CreditCard size={10} color="#666" />
              )}
              <Text style={styles.paymentBadgeText}>{getPaymentLabel(paymentMethod)}</Text>
            </View>
            {isExpanded ? (
              <ChevronUp size={16} color="#999" style={{ marginTop: 4 }} />
            ) : (
              <ChevronDown size={16} color="#999" style={{ marginTop: 4 }} />
            )}
          </View>
        </TouchableOpacity>

        {isExpanded && (
          <View style={styles.expandedContent}>
            <View style={styles.itemsDivider} />
            {sale.sale_items.map((item) => {
              const isRefunded = item.refunded
              const isSelected = selectedItems.has(item.id)
              return (
                <TouchableOpacity
                  key={item.id}
                  style={[styles.itemRow, isRefunded && styles.itemRefunded]}
                  onPress={() => !isRefunded && !isVoided && toggleItemSelection(item.id)}
                  disabled={isRefunded || isVoided}
                  activeOpacity={0.7}
                >
                  {!isVoided && !isRefunded && (
                    <View style={[styles.checkbox, isSelected && styles.checkboxSelected]}>
                      {isSelected && <Check size={14} color="#fff" />}
                    </View>
                  )}
                  <View style={styles.itemInfo}>
                    <Text
                      style={[styles.itemTitle, isRefunded && styles.itemTitleRefunded]}
                      numberOfLines={1}
                    >
                      {getProductTitle(item)}
                    </Text>
                  </View>
                  <View style={styles.itemPriceCol}>
                    <Text style={[styles.itemPrice, isRefunded && styles.itemPriceRefunded]}>
                      {item.price.toFixed(2)} €
                    </Text>
                    {isRefunded && (
                      <View style={styles.returnedBadge}>
                        <Text style={styles.returnedBadgeText}>
                          {t('returned') || 'Returned'}
                        </Text>
                      </View>
                    )}
                  </View>
                </TouchableOpacity>
              )
            })}

            {/* Actions */}
            {!isVoided && (
              <View style={styles.actionRow}>
                {hasReturnableItems && selectedItems.size > 0 && (
                  <TouchableOpacity
                    style={styles.returnButton}
                    onPress={() => handlePartialReturn(sale)}
                    disabled={processing}
                  >
                    <RotateCcw size={14} color={merchantColors.accent} />
                    <Text style={styles.returnButtonText}>
                      {t('return_selected') || 'Return Selected'} ({selectedItems.size})
                    </Text>
                  </TouchableOpacity>
                )}
                <TouchableOpacity
                  style={styles.voidButton}
                  onPress={() => handleVoidSale(sale)}
                  disabled={processing}
                >
                  <Ban size={14} color="#C62828" />
                  <Text style={styles.voidButtonText}>{t('void_sale') || 'Void Sale'}</Text>
                </TouchableOpacity>
              </View>
            )}
          </View>
        )}
      </View>
    )
  }

  if (loading) {
    return (
      <YStack f={1} jc="center" ai="center" bg="$background">
        <Spinner size="large" />
      </YStack>
    )
  }

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity style={styles.backButton} onPress={() => navigation.goBack()}>
          <ArrowLeft size={20} color={merchantColors.primary} />
        </TouchableOpacity>
        <View style={styles.headerTitleContainer}>
          <Text style={styles.headerTitle}>{t('transaction_history') || 'Transaction History'}</Text>
        </View>
        <TouchableOpacity
          style={styles.refreshButton}
          onPress={() => {
            setRefreshing(true)
            fetchSales()
          }}
        >
          <RefreshCw size={20} color={merchantColors.primary} />
        </TouchableOpacity>
      </View>

      {/* Search */}
      <View style={styles.searchContainer}>
        <Search size={18} color="#999" />
        <TextInput
          style={styles.searchInput}
          value={searchQuery}
          onChangeText={setSearchQuery}
          placeholder={t('search') || 'Search...'}
          placeholderTextColor="#999"
        />
        {searchQuery.length > 0 && (
          <TouchableOpacity onPress={() => setSearchQuery('')}>
            <X size={18} color="#999" />
          </TouchableOpacity>
        )}
      </View>

      {filteredSales.length === 0 ? (
        <View style={styles.emptyContainer}>
          <Receipt size={48} color="#ccc" />
          <Text style={styles.emptyText}>{t('no_transactions') || 'No transactions yet'}</Text>
        </View>
      ) : (
        <ScrollView
          style={styles.content}
          contentContainerStyle={styles.contentContainer}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={() => {
                setRefreshing(true)
                fetchSales()
              }}
              colors={[merchantColors.primary]}
              tintColor={merchantColors.primary}
            />
          }
        >
          {groupedSales.map((group, gi) => (
            <View key={gi}>
              <Text style={styles.dayHeader}>{group.label}</Text>
              {group.sales.map(renderSaleCard)}
            </View>
          ))}
          <View style={{ height: 100 }} />
        </ScrollView>
      )}
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F8F7F4',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 12,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#E0E2DB',
  },
  backButton: { padding: 8 },
  headerTitleContainer: { flex: 1, marginLeft: 8 },
  headerTitle: { fontSize: 20, fontWeight: 'bold', color: '#1a1a1a' },
  refreshButton: { padding: 8 },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff',
    marginHorizontal: 16,
    marginTop: 12,
    paddingHorizontal: 12,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#E0E2DB',
  },
  searchInput: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 8,
    fontSize: 16,
    color: '#1a1a1a',
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
  },
  emptyText: {
    fontSize: 16,
    color: '#999',
    marginTop: 16,
    textAlign: 'center',
  },
  content: { flex: 1 },
  contentContainer: { padding: 16, paddingBottom: 100 },
  dayHeader: {
    fontSize: 14,
    fontWeight: '700',
    color: '#666',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginTop: 16,
    marginBottom: 8,
    paddingHorizontal: 4,
  },
  saleCard: {
    backgroundColor: '#fff',
    borderRadius: 12,
    marginBottom: 10,
    borderWidth: 1,
    borderColor: '#E0E2DB',
    overflow: 'hidden',
  },
  saleCardVoided: {
    opacity: 0.6,
    backgroundColor: '#FAFAFA',
  },
  saleHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: 14,
  },
  saleHeaderLeft: { flex: 1 },
  receiptRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  receiptNumber: {
    fontSize: 15,
    fontWeight: '600',
    color: '#1a1a1a',
  },
  dimmedText: { color: '#999' },
  saleTime: {
    fontSize: 13,
    color: '#666',
    marginTop: 2,
  },
  itemCount: {
    fontSize: 12,
    color: '#999',
    marginTop: 2,
  },
  saleHeaderRight: {
    alignItems: 'flex-end',
  },
  saleTotal: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1a1a1a',
  },
  totalStrikethrough: {
    textDecorationLine: 'line-through',
    color: '#999',
  },
  paymentBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 12,
    marginTop: 4,
    backgroundColor: '#F0F0F0',
  },
  cashBadge: { backgroundColor: '#E8F5E9' },
  cardBadge: { backgroundColor: '#E3F2FD' },
  terminalBadge: { backgroundColor: '#F3E5F5' },
  paymentBadgeText: {
    fontSize: 11,
    fontWeight: '600',
    color: '#666',
  },
  voidedBadge: {
    backgroundColor: '#FFEBEE',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 8,
  },
  voidedBadgeText: {
    fontSize: 10,
    fontWeight: '700',
    color: '#C62828',
  },
  expandedContent: {
    paddingHorizontal: 14,
    paddingBottom: 14,
  },
  itemsDivider: {
    height: 1,
    backgroundColor: '#E0E2DB',
    marginBottom: 10,
  },
  itemRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
  },
  itemRefunded: {
    opacity: 0.5,
  },
  checkbox: {
    width: 22,
    height: 22,
    borderRadius: 6,
    borderWidth: 2,
    borderColor: '#CCC',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 10,
  },
  checkboxSelected: {
    backgroundColor: merchantColors.accent,
    borderColor: merchantColors.accent,
  },
  itemInfo: { flex: 1 },
  itemTitle: {
    fontSize: 14,
    fontWeight: '500',
    color: '#1a1a1a',
  },
  itemTitleRefunded: {
    textDecorationLine: 'line-through',
    color: '#999',
  },
  itemPriceCol: {
    alignItems: 'flex-end',
  },
  itemPrice: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1a1a1a',
  },
  itemPriceRefunded: {
    textDecorationLine: 'line-through',
    color: '#999',
  },
  returnedBadge: {
    backgroundColor: '#FFF3E0',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 8,
    marginTop: 2,
  },
  returnedBadgeText: {
    fontSize: 10,
    fontWeight: '600',
    color: '#E65100',
  },
  actionRow: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 12,
  },
  returnButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#E8F5E9',
    paddingVertical: 10,
    borderRadius: 10,
    gap: 6,
  },
  returnButtonText: {
    fontSize: 13,
    fontWeight: '600',
    color: merchantColors.accent,
  },
  voidButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FFEBEE',
    paddingVertical: 10,
    borderRadius: 10,
    gap: 6,
  },
  voidButtonText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#C62828',
  },
})
